import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

const CART_STORAGE_KEY = "cartItems";
const WISHLIST_STORAGE_KEY = "wishlistItems";

const canUseStorage = () => typeof window !== "undefined";

const readStoredArray = (key) => {
  if (!canUseStorage()) return [];

  try {
    const value = window.localStorage.getItem(key);
    const parsedValue = value ? JSON.parse(value) : [];
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch {
    return [];
  }
};

const persistArray = (key, value) => {
  if (!canUseStorage()) return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be unavailable in private mode or when the quota is full.
  }
};

const getProductId = (value) =>
  value && typeof value === "object" && "id" in value ? value.id : value;

const sameProductId = (firstId, secondId) =>
  String(getProductId(firstId)) === String(getProductId(secondId));

const sameCartLine = (item, productId, size, color, personalisation = "") =>
  sameProductId(item.id, productId) &&
  (item.size || "") === (size || "") &&
  (item.color || "") === (color || "") &&
  (item.personalisation || "") === (personalisation || "");

const defaultValue = {
  cartItems: [],
  wishlistItems: [],
  addToCart: () => {},
  removeFromCart: () => {},
  removeCart: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  addToWishlist: () => {},
  removeFromWishlist: () => {},
  toggleWishlist: () => {},
  hasInWishlist: () => false,
  wishlist: {
    items: [],
    add: () => {},
    remove: () => {},
    toggle: () => {},
    has: () => false,
  },
};

export const CartContext = createContext(defaultValue);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStoredArray(CART_STORAGE_KEY)
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    readStoredArray(WISHLIST_STORAGE_KEY)
  );

  useEffect(() => {
    persistArray(CART_STORAGE_KEY, cartItems);
  }, [cartItems]);

  useEffect(() => {
    persistArray(WISHLIST_STORAGE_KEY, wishlistItems);
  }, [wishlistItems]);

  const addToCart = useCallback(
    (productId, quantity = 1, size = "", color = "", personalisation = "") => {
      const parsedQuantity = Number(quantity);
      const quantityToAdd =
        Number.isFinite(parsedQuantity) && parsedQuantity > 0
          ? Math.floor(parsedQuantity)
          : 1;

      setCartItems((previousItems) => {
        const existingItem = previousItems.find((item) =>
          sameCartLine(item, productId, size, color, personalisation)
        );

        if (!existingItem) {
          return [
            ...previousItems,
            {
              id: productId,
              size: size || "",
              color: color || "",
              personalisation: personalisation || "",
              quantity: quantityToAdd,
            },
          ];
        }

        return previousItems.map((item) =>
          sameCartLine(item, productId, size, color, personalisation)
            ? {
                ...item,
                quantity:
                  (Number.isFinite(Number(item.quantity))
                    ? Number(item.quantity)
                    : 0) + quantityToAdd,
              }
            : item
        );
      });
    },
    []
  );

  const removeFromCart = useCallback(
    (productId, size = "", color = "", personalisation = "") => {
      setCartItems((previousItems) =>
        previousItems.filter(
          (item) =>
            !sameCartLine(item, productId, size, color, personalisation)
        )
      );
    },
    []
  );

  const updateQuantity = useCallback(
    (productId, size = "", color = "", quantity = 1, personalisation = "") => {
      const parsedQuantity = Number(quantity);
      const nextQuantity =
        Number.isFinite(parsedQuantity) && parsedQuantity > 0
          ? Math.floor(parsedQuantity)
          : 1;

      setCartItems((previousItems) =>
        previousItems.map((item) =>
          sameCartLine(item, productId, size, color, personalisation)
            ? { ...item, quantity: nextQuantity }
            : item
        )
      );
    },
    []
  );

  const clearCart = useCallback(() => setCartItems([]), []);

  const addToWishlist = useCallback((productId) => {
    setWishlistItems((previousItems) =>
      previousItems.some((id) => sameProductId(id, productId))
        ? previousItems
        : [...previousItems, productId]
    );
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    setWishlistItems((previousItems) =>
      previousItems.filter((id) => !sameProductId(id, productId))
    );
  }, []);

  const toggleWishlist = useCallback((productId) => {
    setWishlistItems((previousItems) =>
      previousItems.some((id) => sameProductId(id, productId))
        ? previousItems.filter((id) => !sameProductId(id, productId))
        : [...previousItems, productId]
    );
  }, []);

  const hasInWishlist = useCallback(
    (productId) =>
      wishlistItems.some((id) => sameProductId(id, productId)),
    [wishlistItems]
  );

  const contextValue = useMemo(
    () => ({
      cartItems,
      wishlistItems,
      addToCart,
      removeFromCart,
      removeCart: removeFromCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      hasInWishlist,
      wishlist: {
        items: wishlistItems,
        add: addToWishlist,
        remove: removeFromWishlist,
        toggle: toggleWishlist,
        has: hasInWishlist,
      },
    }),
    [
      addToCart,
      addToWishlist,
      cartItems,
      clearCart,
      hasInWishlist,
      removeFromCart,
      removeFromWishlist,
      toggleWishlist,
      updateQuantity,
      wishlistItems,
    ]
  );

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};
